import type { NotionPageKind } from "@seagames/contracts";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { usePageContextMenu } from "../../components/page/page-actions";
import { PageIcon } from "../../components/icons/page-icon";
import { Icon } from "../../components/shell/icons";
import { Button } from "../../components/ui/button";
import { EmptyState } from "../../components/ui/empty-state";
import { Spinner } from "../../components/ui/spinner";
import { toastApiError } from "../../components/ui/toast";
import { formatDateTime, formatShort } from "../../lib/i18n/format";
import { createPage } from "../../lib/notion/mutations";
import { recentQuery } from "../../lib/notion/queries";

/** Home: start something, or go back to what was edited last (by anyone). */
export const Route = createFileRoute("/_app/")({
	component: Home,
});

function Home() {
	const { t } = useTranslation("pages");
	const tn = useTranslation("nav").t;
	const { session } = Route.useRouteContext();
	const navigate = useNavigate();
	const recent = useQuery(recentQuery());
	const menu = usePageContextMenu();
	const [busy, setBusy] = useState<NotionPageKind | null>(null);

	const create = async (kind: NotionPageKind) => {
		setBusy(kind);
		try {
			const page = await createPage({ parentId: null, kind });
			await navigate({ to: "/p/$pageId", params: { pageId: page.id } });
		} catch (e) {
			toastApiError(e);
		} finally {
			setBusy(null);
		}
	};

	return (
		<div className="mx-auto w-full max-w-[56rem] px-[1rem] pt-[2rem] pb-[4rem] sm:px-[3rem] sm:pt-[3.5rem]">
			<section className="wn-hero relative overflow-hidden rounded-[1.25rem] border border-rule px-[1.5rem] pt-[2rem] pb-[1.75rem] sm:px-[2.25rem] sm:pt-[2.5rem]">
			<div aria-hidden="true" className="wn-ribbon absolute inset-x-0 top-0" />
			<h1 className="font-black text-[2rem] text-ink leading-tight tracking-[-0.02em] sm:text-[2.5rem]">{t("home.greeting", { name: session.user.name.split(" ")[0] })}</h1>
			<p className="mt-[0.5rem] max-w-[40rem] text-[1rem] text-muted">{t("home.subtitle")}</p>
			<div className="mt-[1.5rem] flex flex-wrap gap-[0.5rem]">
				<Button loading={busy === "PAGE"} disabled={busy !== null} onClick={() => void create("PAGE")}>
					<Icon name="page" className="size-[1rem]" />
					{t("home.newPage")}
				</Button>
				<Button variant="outline" loading={busy === "BOARD"} disabled={busy !== null} onClick={() => void create("BOARD")}>
					<Icon name="board" className="size-[1rem]" />
					{t("home.newBoard")}
				</Button>
			</div>
			</section>

			<h2 className="mt-[2.5rem] mb-[0.875rem] flex items-center gap-[0.375rem] font-semibold text-[0.75rem] text-muted uppercase tracking-[0.08em]">
				<Icon name="clock" className="size-[0.875rem]" />
				{t("home.recent")}
			</h2>
			{recent.isPending ? (
				<Spinner className="size-[1.25rem] text-muted" label={t("home.loading")} />
			) : recent.isError ? (
				<button type="button" onClick={() => void recent.refetch()} className="cursor-pointer text-conflict underline">
					{t("home.loadError")}
				</button>
			) : recent.data.length === 0 ? (
				<EmptyState title={t("home.emptyTitle")} body={t("home.emptyBody")} />
			) : (
				<ul className="grid grid-cols-1 gap-[0.75rem] sm:grid-cols-2 lg:grid-cols-3">
					{recent.data.map((p) => (
						<li key={p.id}>
							<Link
								{...menu({ id: p.id, title: p.title, kind: p.kind })}
								to="/p/$pageId"
								params={{ pageId: p.id }}
								className="group flex h-full flex-col gap-[0.625rem] rounded-card border border-rule bg-sheet p-[1rem] shadow-[var(--shadow-card)] transition-[transform,box-shadow,border-color] duration-150 hover:-translate-y-0.5 hover:border-ink/20 hover:shadow-[var(--shadow-card-hover)]"
							>
								<span className="flex size-[2.75rem] items-center justify-center rounded-[0.625rem] bg-paper transition-colors group-hover:bg-board-wash">
									<PageIcon icon={p.icon} kind={p.kind} size={1.75} />
								</span>
								<span className="line-clamp-2 font-bold text-[0.9375rem] text-ink">{p.title.trim() || tn("untitled")}</span>
								<span className="mt-auto text-[0.75rem] text-muted" title={formatDateTime(p.updated_at)}>
									{t("home.editedBy", { name: p.updated_by.name, time: formatShort(p.updated_at) })}
								</span>
							</Link>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}
